import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../home/components/Footer";
import { blogPosts } from "../../mocks/homeData";
import { usePageMeta } from "../../hooks/usePageMeta";

const categories = [
  "Tümü",
  "Hangar",
  "Fabrika",
  "Depo",
  "Çelik Konstrüksiyon",
];

export default function BlogPage() {
  usePageMeta({
    title: "Blog | Hangar Muhendislik",
    description:
      "Celik konstruksiyon, fabrika ve depo planlamasi hakkinda teknik ve sektorel yazilar.",
  });

  const [activeCategory, setActiveCategory] = useState("Tümü");

  const filteredPosts = useMemo(() => {
    if (activeCategory === "Tümü") return blogPosts;
    return blogPosts.filter((post) => post.category === activeCategory);
  }, [activeCategory]);

  return (
    <div className="font-body antialiased">
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative h-[420px] md:h-[520px] flex items-center justify-center overflow-hidden bg-navy">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://storage.readdy-site.link/project_files/ca0597d3-e4ee-4889-8ae5-f7591fdfc85d/5e9154ac-a13c-4eba-9ced-bca9c2312323_img-10.jpeg?v=8a3f7d4400da1472d6d58694865bcb9a"
              alt="Blog Arkaplan"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/50 to-navy/70" />
          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <div className="inline-block w-12 h-1 rounded-full bg-copper mb-6" />
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
              Blog & Güncel İçerikler
            </h1>
            <p className="font-body text-base md:text-lg text-white/70 max-w-2xl mx-auto">
              Çelik konstrüksiyon, endüstriyel yapılar ve mühendislik dünyasından güncel bilgiler.
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="bg-white pt-12 md:pt-16 pb-4 md:pb-6">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`font-body text-sm font-medium px-4 md:px-5 py-2 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap border ${
                    activeCategory === cat
                      ? "bg-copper text-white border-copper"
                      : "bg-white text-navy border-gray-200 hover:border-copper hover:text-copper"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <p className="text-center font-body text-sm text-gray-500 mt-4">
              {filteredPosts.length} makale gösteriliyor
            </p>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="bg-white py-10 md:py-16 lg:py-24">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="group bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-copper/20 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="relative h-48 md:h-52 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                      <div className="bg-copper text-white font-body text-xs font-medium px-3 py-1.5 rounded-full">
                        {post.date}
                      </div>
                      {post.category && (
                        <div className="bg-white/90 backdrop-blur-sm text-navy font-body text-xs font-medium px-3 py-1.5 rounded-full">
                          {post.category}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-5 md:p-6">
                    <h3 className="font-heading font-bold text-base md:text-lg text-navy mb-3 line-clamp-2 group-hover:text-copper transition-colors duration-300">
                      {post.title}
                    </h3>
                    <p className="font-body text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-2 font-body text-sm font-medium text-copper hover:text-copper-dark transition-colors group/link cursor-pointer">
                      Devamını Oku
                      <div className="w-4 h-4 flex items-center justify-center group-hover/link:translate-x-1 transition-transform">
                        <i className="ri-arrow-right-line" />
                      </div>
                    </span>
                  </div>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
                  <i className="ri-article-line text-4xl" />
                </div>
                <p className="font-body text-lg text-gray-500">
                  Bu kategoride henüz makale bulunmuyor.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gray-50 py-16 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-navy mb-4">
              Projeniz hakkında bilgi almak ister misiniz?
            </h2>
            <p className="font-body text-base text-gray-600 mb-8 max-w-xl mx-auto">
              Uzman ekibimiz size yardımcı olmaktan memnuniyet duyar.
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-dark text-white font-body text-sm font-medium px-8 py-4 rounded-md transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
            >
              Bize Ulaşın
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
